import { useLoggingService } from '@/services/logging/composable';

const REFRESH_TIMEOUT = 10000;

export function useServiceWorkerUpdate() {
  const loggingService = useLoggingService();

  const isRefreshing = ref(false);
  const registration = ref<null | ServiceWorkerRegistration>(null);
  const updateAvailable = ref(false);

  let timeoutId: number | null = null;

  /**
   * Notify the user that an update is available.
   * The new service worker is installed, but not yet active.
   * Store the ServiceWorkerRegistration instance for later use.
   * Source: https://medium.com/@dougallrich/give-users-control-over-app-updates-in-vue-cli-3-pwas-20453aedc1f2
   */
  function handleUpdateAvailable(e: CustomEvent<ServiceWorkerRegistration>): void {
    registration.value = e.detail;
    updateAvailable.value = true;
    loggingService.info('Service worker update available');
  }

  function refreshApp(): void {
    if (isRefreshing.value) {
      loggingService.warn('Refresh already in progress, ignoring additional refresh request');
      return;
    }

    updateAvailable.value = false;

    if (!registration.value?.waiting) {
      loggingService.error(
        new Error('Service worker registration.waiting is null, forcing page reload'),
        { code: 'UpdateApp.registrationWaitingNull' },
      );
      forceReload();
      return;
    }

    try {
      loggingService.info('Attempting to skip waiting for service worker');

      isRefreshing.value = true;
      registration.value.waiting.postMessage('skipWaiting');

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      // Fallback timeout in case controllerchange doesn't fire
      timeoutId = window.setTimeout(() => {
        if (isRefreshing.value) {
          loggingService.warn(
            'Service worker controllerchange event did not fire within timeout, forcing reload',
          );
          forceReload();
        }
        timeoutId = null;
      }, REFRESH_TIMEOUT);
    } catch (error) {
      loggingService.error(error as Error, { code: 'UpdateApp.refreshApp' });
      forceReload();
    }
  }

  function forceReload(): void {
    loggingService.info('Forcing page reload');
    isRefreshing.value = true;

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }

    window.location.reload();
  }

  // Refresh all open app tabs when a new service worker is installed.
  function handleControllerChange(): void {
    loggingService.info('Service worker controller changed, reloading page');

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }

    window.location.reload();
  }

  function handlePWAInstall(): void {
    loggingService.trackEvent(new loggingService.AnalyticEventCategories.PWAInstalled());
  }

  function setupServiceWorkerListeners(): void {
    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      loggingService.warn('Service worker not supported in this browser');
      return;
    }

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    document.addEventListener('swUpdated', handleUpdateAvailable as EventListener, { once: true });
    window.addEventListener('appinstalled', handlePWAInstall);
  }

  function cleanup(): void {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    }
    document.removeEventListener('swUpdated', handleUpdateAvailable as EventListener);
    window.removeEventListener('appinstalled', handlePWAInstall);
  }

  onMounted(() => {
    setupServiceWorkerListeners();
  });

  onUnmounted(() => {
    cleanup();
  });

  return {
    updateAvailable: readonly(updateAvailable),
    isRefreshing: readonly(isRefreshing),
    refreshApp,
  };
}
